import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PopoverNext } from '@momentum-ui/react-collaboration';
import { useLineSelection } from './hooks/useLineSelection';
import { PhoneServicesLineService } from './services/PhoneServicesLineService';
import { UcmLineService } from './services/UcmLineService';
import { MockCallerIdService } from './services/MockCallerIdService';
import { ICallerIdService } from './interfaces/ICallerIdService';
import { isLineSelectionFeatureEnabled } from './utils/featureGate';
import { LineSelectorItem } from './LineSelectorItem';
import { getMultiLineStyle } from '../../packages/widgets/src/utils/handleMultiLineLabel';
import { submitMetrics } from '../../packages/widgets/src/services/WebexMetricsService';
import { getCallingBackEnd, CALLING_BACKEND } from '../../packages/widgets/src/utils/callingUtils';

import { Logger } from '../../packages/logger/src';
import './LineSelector.scss';

/**
 * Self-contained line selector component.
 *
 * Figma: https://www.figma.com/design/jGgOmoHi2eDwX1FX0fKbNi/Multiline--queue--hunt-group-indicator-in-MS-team---voicemail-call-back?node-id=2415-46110&t=04vc2c4cfQ6B6mOn-0
 *
 * Drop-in usage: <LineSelector />
 * No props required. Handles its own:
 * - Feature gate checking (org ID)
 * - Data fetching (Phone Services for WXC)
 * - State management (useLineSelection hook)
 * - Visibility (hides when single line or feature disabled)
 * - Rendering (trigger button + popover)
 *
 * Wiring: creates concrete dependencies internally.
 * For testing, use <LineSelectorInner service={mockService} /> (see below).
 *
 * Dev/testing mode:
 *   window.USE_MOCK_LINE_SELECTION = true  (then refresh)
 *   Bypasses feature gate + uses MockCallerIdService with 4 demo lines.
 *   Optional: window.MOCK_LINE_SELECTION_FAIL_GET = true  (simulate GET failure)
 *   Optional: window.MOCK_LINE_SELECTION_FAIL_PUT = true  (simulate PUT failure → rollback)
 */
export const LineSelector = () => {
  // Check localStorage first (persists across reloads), then window global
  const useMock = false/*!!(
    typeof window !== 'undefined' &&
    (localStorage.getItem('USE_MOCK_LINE_SELECTION') === 'true' || (window as any).USE_MOCK_LINE_SELECTION)
  );*/
  const [featureEnabled, setFeatureEnabled] = useState<boolean | null>(useMock ? true : null);

  useEffect(() => {
    if (useMock) return;
    let cancelled = false;

    isLineSelectionFeatureEnabled()
      .then((enabled) => {
        if (!cancelled) {
          Logger.info(`[LineSelection] Feature gate resolved: ${enabled}`);
          setFeatureEnabled(enabled);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          Logger.error(`[LineSelection] Feature gate check failed: ${err}`);
          setFeatureEnabled(false);
        }
      });

    return () => { cancelled = true; };
  }, [useMock]);

  // Wire up concrete dependencies (composition root for this component).
  //  - WXC: lines come from Phone Services Mercury WebSocket push
  //  - UCM: lines come from UC Config REST API (see UCM_LINE_SELECTION_DESIGN.md)
  // NOTE: useMemo must be called unconditionally (React Rules of Hooks).
  const service = useMemo<ICallerIdService | null>(() => {
    if (useMock) {
      return new MockCallerIdService();
    }
    const backend = getCallingBackEnd();
    Logger.info(`[LineSelection] Creating service for backend: ${backend}`);
    if (backend === CALLING_BACKEND.WXC) {
      return new PhoneServicesLineService();
    }
    if (backend === CALLING_BACKEND.UCM) {
      return new UcmLineService();
    }
    Logger.info(`[LineSelection] Multi-line not supported for backend: ${backend}`);
    return null;
  }, [useMock]);

  // Wait for feature toggle check, then gate on result
  if (featureEnabled === null || !featureEnabled) {
    return null;
  }

  if (!service) return null;

  return (
    <LineSelectorInner service={service} />
  );
};

/**
 * Inner component with injected service — enables testing with mock service.
 * Exported separately for test usage.
 */
export const LineSelectorInner = ({ service }: { service: ICallerIdService }) => {
  const { t } = useTranslation();
  const { lines, selectedLine, selectLine, isLoading, hasMultipleLines } = useLineSelection(service);
  const [popoverInstance, setPopoverInstance] = useState<any>();
  const [announcement, setAnnouncement] = useState('');
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Reset focus when popover closes, set focus to selected item when opening
  useEffect(() => {
    if (!isOpen) {
      setFocusedId(null);
    } else {
      // When opening, set focus to the currently selected item
      const selectedLine = lines.find(l => l.isSelected);
      if (selectedLine) {
        // If line has callerIds, focus the selected callerID; otherwise focus the line
        if (selectedLine.callerIds && selectedLine.callerIds.length > 0) {
          const selectedCid = selectedLine.callerIds.find(c => c.isSelected);
          if (selectedCid) {
            setFocusedId(`${selectedLine.lineIndex}-cid-${selectedCid.callerIdIndex}`);
          } else {
            // Fallback to first caller ID if none are marked selected
            setFocusedId(`${selectedLine.lineIndex}-cid-${selectedLine.callerIds[0].callerIdIndex}`);
          }
        } else {
          setFocusedId(`line-${selectedLine.lineIndex}`);
        }
      }
    }
  }, [isOpen, lines]);

  // Auto-focus the focused item when focusedId changes
  useEffect(() => {
    if (focusedId && isOpen) {
      const element = document.getElementById(focusedId);
      if (element) {
        element.focus();
      }
    }
  }, [focusedId, isOpen]);

  // Hide if loading, no lines, or single line
  if (isLoading || !hasMultipleLines || !selectedLine) return null;

  const badgeStyle = getMultiLineStyle(selectedLine.lineLabel);

  return (
    <div className="line-selector">
      <PopoverNext
        trigger="click"
        placement="bottom"
        showArrow
        interactive
        hideOnEsc
        onShow={() => {
          setIsOpen(true);
          submitMetrics({ metricEvent: 'lineSelection.popoverOpened' });
        }}
        onHide={() => setIsOpen(false)}
        triggerComponent={
          <button
            ref={triggerRef}
            className="line-selector__trigger"
            aria-label={`${t('lineSelector.callAs')}: ${selectedLine.lineLabel} ${selectedLine.number || selectedLine.title}`}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            data-testid="line-selector-trigger"
          >
            <span className="line-selector__badge" style={badgeStyle}>
              {selectedLine.lineLabel}
            </span>
            <span className="line-selector__name">{selectedLine.number || selectedLine.title}</span>
            <span className="line-selector__chevron">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.14645 5.64645C4.34171 5.45118 4.65829 5.45118 4.85355 5.64645L8 8.79289L11.1464 5.64645C11.3417 5.45118 11.6583 5.45118 11.8536 5.64645C12.0488 5.84171 12.0488 6.15829 11.8536 6.35355L8.35355 9.85355C8.15829 10.0488 7.84171 10.0488 7.64645 9.85355L4.14645 6.35355C3.95118 6.15829 3.95118 5.84171 4.14645 5.64645Z"
                  fill="currentColor" />
              </svg>
            </span>
          </button>
        }
        setInstance={setPopoverInstance}
      >
        <div
          className="line-selector__popover"
          role="listbox"
          aria-label={`${t('lineSelector.callAs')}, ${lines.length} ${lines.length === 1 ? t('lineSelector.lineSingular') : t('lineSelector.linePlural')} ${t('lineSelector.linesAvailable')}`}
          onKeyDown={(e) => {
            // When Escape is pressed in the listbox, close the popover and return focus to trigger
            if (e.key === 'Escape') {
              e.preventDefault();
              setIsOpen(false);
              popoverInstance?.hide();
              // Return focus to trigger button after closing
              setTimeout(() => {
                triggerRef.current?.focus();
              }, 0);
              return;
            }

            // Trap Tab key: if focus is in listbox, keep it here and cycle through items
            if (e.key === 'Tab') {
              const listboxDiv = e.currentTarget as HTMLElement;
              const selectableItems = Array.from(listboxDiv.querySelectorAll('[role="option"]')) as HTMLElement[];
              if (selectableItems.length === 0) return;

              // Always prevent Tab from leaving the listbox
              e.preventDefault();

              const activeElement = document.activeElement as HTMLElement;
              const currentIndex = selectableItems.indexOf(activeElement);

              if (currentIndex !== -1) {
                // Focus is on an item, cycle through items
                let nextIndex = e.shiftKey
                  ? currentIndex === 0 ? selectableItems.length - 1 : currentIndex - 1
                  : currentIndex === selectableItems.length - 1 ? 0 : currentIndex + 1;
                selectableItems[nextIndex]?.focus();
              } else {
                // Focus is not on an item, move to first item
                selectableItems[0]?.focus();
              }
            }
          }}
        >
          <ul>
            {lines.map((line) => (
              <LineSelectorItem
                key={line.lineIndex}
                line={line}
                focusedId={focusedId}
                onFocusChange={setFocusedId}
                onSelectLine={async (callerIdIndex?: number) => {
                  // Determine what to announce based on caller ID selection
                  let selectedLabel = `${line.lineLabel} ${line.title || line.number}`;
                  if (callerIdIndex !== undefined && line.callerIds) {
                    const selectedCid = line.callerIds.find(c => c.callerIdIndex === callerIdIndex);
                    if (selectedCid) {
                      selectedLabel = `${line.lineLabel} ${selectedCid.name} ${selectedCid.number}`;
                    }
                  }

                  // Await selection to complete; only announce if successful (returns true).
                  // If selectLine() fails, it returns false, UI is rolled back, and no announcement is made.
                  // Screen reader users always hear truthful feedback matching the UI state.
                  const success = await selectLine(line.lineIndex, callerIdIndex);
                  if (success) {
                    setAnnouncement(`${t('lineSelector.callAs')}: ${selectedLabel}`);
                    setIsOpen(false);
                    popoverInstance?.hide();
                    // Return focus to trigger button after selection
                    setTimeout(() => {
                      triggerRef.current?.focus();
                    }, 0);
                  }
                }}
              />
            ))}
          </ul>
        </div>
      </PopoverNext>
      <div
        aria-live="polite"
        aria-atomic="true"
        className="line-selector__sr-only"
      >
        {announcement}
      </div>
    </div>
  );
};

export default LineSelector;
